// ─── COLOUR TOKENS ────────────────────────────────────────
export const C = {
  navy:    "#0F0F2A",
  navyMid: "#1A1A3E",
  card:    "#1E1E45",
  pink:    "#E83E8C",
  orange:  "#FF6B35",
  teal:    "#00C2A8",
  gold:    "#FFB700",
  purple:  "#9B6DFF",
  white:   "#FFFFFF",
  light:   "#E8E8F8",
  muted:   "#8080A8",
  dim:     "#3A3A5C",
  green:   "#22C55E",
  red:     "#EF4444",
};

// Returns dark or light palette — accents stay the same in both modes
export function getColors(isDark) {
  if (isDark) return C;
  return {
    ...C,
    navy:    "#F4F4FF",
    navyMid: "#EAEAF8",
    card:    "#FFFFFF",
    white:   "#0F0F2A",
    light:   "#1A1A3E",
    muted:   "#5555A8",
    dim:     "#C4C4E0",
  };
}

// ─── PATHS ────────────────────────────────────────────────
export const paths = [
  {
    id: "puzzle",
    label: "Puzzle Path",
    icon: "🧩",
    tags: ["LOW INTENSITY", "60 MIN", "PROBLEM SOLVING", "TEAMWORK", "LOGIC"],
    demand: "Low physical demand · Focus on teamwork & problem-solving",
    duration: "60 min",
    desc: "Solve clues between obstacles to unlock the next station. Problem-solving + teamwork.",
    color: C.teal,
    narrative: "Operation Red Sky",
    mission:
      "A signal has been intercepted from an unknown source inside the facility. Your team has 60 minutes to decode the transmission hidden across 6 stations before it self-destructs.",
    finaleText: (cp) =>
      `The transmission has been decoded. Threat neutralised. Operation Red Sky — success. You completed ${cp} of 6 checkpoints.`,
    nextMission: {
      name: "Operation Deep Freeze",
      icon: "🧊",
      chapter: "CHAPTER 2",
      teaser: "The signal wasn't generated inside the facility — it was a breadcrumb, left deliberately. Someone wanted you to find it. The real source has been traced to a vault 40 metres below ground... and it's already been accessed.",
    },
    nextMissionHook: () =>
      "Your decoded intel is already uploaded to HQ. Session 2 picks up exactly where your team left off.",
  },
  {
    id: "action",
    label: "Action Sprint",
    icon: "⚡",
    tags: ["HIGH INTENSITY", "45 MIN", "SPEED FOCUSED", "NARRATIVE DRIVEN", "STAKES"],
    demand: "High physical demand · Speed-focused with narrative stakes",
    duration: "45 min",
    desc: "Race the clock to rescue your squad. Every second is a life. Beat the time — save an ally.",
    color: C.orange,
    narrative: "Velocity Protocol",
    mission:
      "The structure is failing. Your squad of 6 is scattered across the facility. You have one chance to reach each of them before their section collapses. Move fast — every second is a life.",
    finaleText: (saved) =>
      saved >= 6
        ? "Perfect rescue. All six made it out. Your squad is intact — legendary performance."
        : saved >= 4
        ? `You saved ${saved} of 6. Strong effort. Come back to rescue the rest.`
        : `You saved ${saved} of 6. The mission was brutal. Return and bring everyone home.`,
    nextMission: {
      name: "Night Run Protocol",
      icon: "🌙",
      chapter: "CHAPTER 2",
      teaser: "The structure held — barely. But the collapse exposed a tunnel network beneath the facility. Someone used it to escape before you arrived. The next mission: go in dark, find out who set the trap.",
    },
    nextMissionHook: (saved) =>
      saved >= 6
        ? "Perfect rescue unlocked the full debrief. You're first on the Night Run roster."
        : `${6 - saved} team member${6 - saved > 1 ? "s are" : " is"} still unaccounted for. Session 2 is your chance to bring them home.`,
  },
  {
    id: "gamer",
    label: "Hero's Journey",
    icon: "🎮",
    tags: ["MODERATE INTENSITY", "60 MIN", "XP", "DIGITAL", "PROGRESSION"],
    demand: "Moderate physical demand · RPG progression system",
    duration: "60 min",
    desc: "Earn XP, unlock gear, and level up your hero as you complete obstacles. Slay the dragon.",
    color: C.purple,
    narrative: "Dragon's Keep",
    mission:
      "Your kingdom has fallen under the shadow of the dragon. Journey through the Keep's six chambers to gather your power. Only a fully equipped hero can face what awaits at the end.",
    finaleText: (gear) =>
      gear >= 6
        ? "Fully equipped. The dragon falls. Your kingdom is restored — Champion of the Keep."
        : `You collected ${gear} pieces of gear. Return to complete your loadout and finish the dragon.`,
    nextMission: {
      name: "The Shadow Realm",
      icon: "🌑",
      chapter: "CHAPTER 2",
      teaser: "The dragon is gone — but the Keep is darker than before. Something followed it back through the gate. Your hero's power is all that stands between the realm and what comes next. You'll need everything you've earned.",
    },
    nextMissionHook: (gear) =>
      gear >= 6
        ? "Full loadout achieved. Your hero is cleared for Shadow Realm entry — maximum power."
        : `You're carrying ${gear}/6 gear pieces. Return to complete your loadout before the boss fight.`,
  },
];

// ─── CHECKPOINTS ──────────────────────────────────────────
export const checkpoints = [
  {
    id: 1,
    obstacle: "Climbing Wall",
    type: "Physical challenge",
    xp: 150,
    // Puzzle path
    clue: "I have cities, but no houses live there. Mountains, but no trees grow there. What am I?",
    answer: "map",
    hint: "Think about what explorers use to navigate — something that represents places without being a place itself.",
    // Action path
    actionTime: 90,
    ally: { name: "Reyes", role: "Scout", emoji: "🧭" },
    allyContext: "Reyes is pinned at the top of the wall. The ceiling is coming down. You have 90 seconds to reach her before the section collapses.",
    // Gamer path
    gear: { name: "Iron Shield", emoji: "🛡️", desc: "Your first line of defence. The fortress is breached — protect your team." },
    gamerChallenge: {
      question: "To earn your Shield, you must prove your worth. What does a true shield protect?",
      options: ["The body only", "The body AND those behind you", "Only the strongest warrior", "Attack is the best defence"],
      correct: 1,
    },
    // Story beats
    story: {
      puzzle: "A signal has been intercepted from the north tower. Climb the rockface and scan the coordinates etched at the top.",
      action: "First checkpoint. Hit the wall, reach the top, tag the sensor. Reyes is waiting — your split starts now.",
      gamer: "The dragon's fortress wall. Climb to the top and unlock your Iron Shield — your first line of defence.",
    },
  },
  {
    id: 2,
    obstacle: "Salmon Ladder",
    type: "Upper body",
    xp: 200,
    clue: "The more you take, the more you leave behind. What am I?",
    answer: "footsteps",
    hint: "Think about something that follows you everywhere — invisible, yet always present wherever you've been.",
    actionTime: 80,
    ally: { name: "Mira", role: "Medic", emoji: "⚕️" },
    allyContext: "Mira is cut off at the ladder crossing. Rising debris is blocking the passage. You have 80 seconds to reach him before the path is gone.",
    gear: { name: "Ancient Scroll", emoji: "📜", desc: "Lost knowledge recovered. You can now read the dragon's true weakness." },
    gamerChallenge: {
      question: "The Ancient Scroll contains lost knowledge. What does the inscription say a hero must carry above all?",
      options: ["Power and strength", "Knowledge of the enemy", "Courage and wisdom", "The fastest weapon"],
      correct: 2,
    },
    story: {
      puzzle: "The transmission is fragmented. Navigate the rungs to retrieve the encrypted fragment submerged in the data stream.",
      action: "Upper body test. Each rung is a second on the clock. Mira needs you — don't slow down.",
      gamer: "Cross the enchanted river. Each rung adds strength — the Ancient Scroll unlocks the dragon's true weakness.",
    },
  },
  {
    id: 3,
    obstacle: "Pipe Traverse",
    type: "Grip strength",
    xp: 175,
    clue: "I speak without a mouth and hear without ears. I have no body, but I come alive with wind. What am I?",
    answer: "echo",
    hint: "This happens in canyons and tunnels — your voice returns to you from the walls.",
    actionTime: 70,
    ally: { name: "Tomas", role: "Engineer", emoji: "🔧" },
    allyContext: "Tomas is trapped in the pipe corridor. The walls are closing in. You have 70 seconds to navigate through before he's sealed inside.",
    gear: { name: "Stealth Cloak", emoji: "🧥", desc: "Move unseen. The dragon cannot track what it cannot see." },
    gamerChallenge: {
      question: "To unlock the Stealth Cloak you must pass unseen. What is the first rule of stealth?",
      options: ["Move fast", "Strike first", "Move without making a sound", "Hide in the shadows and wait"],
      correct: 2,
    },
    story: {
      puzzle: "The signal echoes through the ventilation network. Traverse the pipes and identify the repeating pattern — it's a code.",
      action: "Full hang. No breaks. Timer running. Your arms are burning — push through it. Tomas is counting on you.",
      gamer: "The whispering corridor. Traverse the pipes and your hero unlocks the Stealth Cloak 🧥.",
    },
  },
  {
    id: 4,
    obstacle: "Monkey Bars",
    type: "Overhead traverse",
    xp: 200,
    clue: "The more you take, the more you leave behind. I follow you everywhere, silent but faithful. What am I?",
    answer: "shadow",
    hint: "It's always attached to you on a sunny day — look down and you'll see it beneath your feet.",
    actionTime: 75,
    ally: { name: "Jin", role: "Hacker", emoji: "💻" },
    allyContext: "Jin is stranded on the overhead grid. The floor below is giving way. You have 75 seconds to cross and pull her to safety.",
    gear: { name: "Wings of Ascension", emoji: "🦅", desc: "Rise above the canopy. Your hero gains flight — the dragon's air advantage is neutralised." },
    gamerChallenge: {
      question: "To earn the Wings of Ascension, you must trust the air. What does a bird rely on above all to fly?",
      options: ["Its talons", "Its nest and rest", "The wind beneath its wings", "Raw speed alone"],
      correct: 2,
    },
    story: {
      puzzle: "The decoded coordinates point upward. Cross the overhead grid to reach the relay point and download the next sequence.",
      action: "Overhead traverse. Stay loose, stay fast. Jin is stranded up there — cumulative time is climbing.",
      gamer: "Rise above the canopy. Cross the sky bridge and your Wings of Ascension unlock 🦅.",
    },
  },
  {
    id: 5,
    obstacle: "Warped Wall",
    type: "Full body / run-up",
    xp: 225,
    clue: "I have hands but cannot clap. I have a face but no eyes. Without me, you'd miss every meeting. What am I?",
    answer: "clock",
    hint: "Think about something that tracks time — it has numbers and moves, but can't feel anything.",
    actionTime: 60,
    ally: { name: "Dex", role: "Pilot", emoji: "✈️" },
    allyContext: "Dex is on the other side of the wall. The structure is tilting — his evac route closes in 60 seconds. Break through before the section snaps.",
    gear: { name: "Power Surge", emoji: "⚡", desc: "Maximum power unlocked. Your attack strength doubles — the dragon's armour can now be cracked." },
    gamerChallenge: {
      question: "The Power Surge requires pure momentum. What fuels a true surge of energy?",
      options: ["Waiting for the perfect moment", "Fear of failure", "Momentum and unbroken will", "Resting before you act"],
      correct: 2,
    },
    story: {
      puzzle: "The final barrier before the source. Break through to access the transmission hub on the other side.",
      action: "The wall. Everyone faces it. Run hard, hit the curve, grab the ledge. Dex has 60 seconds — own it.",
      gamer: "The dragon stirs. Power surge from the barrier wall — break through and your power meter hits max ⚡.",
    },
  },
  {
    id: 6,
    obstacle: "Ball Pit Finale",
    type: "Navigation / endurance",
    xp: 250,
    clue: "I'm always running but never move. I have banks but no money. I carve through rock but have no hands. What am I?",
    answer: "river",
    hint: "Think about something in nature that flows constantly — it has two sides and carves the landscape.",
    actionTime: 45,
    ally: { name: "Atlas", role: "Commander", emoji: "🎖️" },
    allyContext: "Atlas is in the final chamber. The whole facility is going down in 45 seconds. This is the last chance — reach him now.",
    gear: { name: "Dragon Sword", emoji: "🗡️", desc: "The final weapon. Forged from the combined power of your journey. The dragon falls." },
    gamerChallenge: {
      question: "The Dragon Sword is forged for one purpose. How does the dragon truly fall?",
      options: ["By speed alone", "By the combined power of your entire journey", "By strength alone", "By luck and timing"],
      correct: 1,
    },
    story: {
      puzzle: "The source is found. Navigate through the interference field and confirm the signal origin. Operation Red Sky — complete.",
      action: "Final push. Dive in, navigate through, hit the exit sensor. Atlas is the last one. Your final time is set.",
      gamer: "The final arena. The dragon waits in the field of chaos. Navigate through and deliver the killing blow 🐉.",
    },
  },
];

// ─── QUIZ DATA ────────────────────────────────────────────
export const quizQuestions = [
  {
    question: "How do you want to tackle today's challenge?",
    options: [
      { label: "As a team, solving puzzles together", path: "puzzle" },
      { label: "Racing the clock to beat my best time", path: "action" },
      { label: "Levelling up a character as I go", path: "gamer" },
    ],
  },
  {
    question: "What sounds most exciting right now?",
    options: [
      { label: "Cracking a mystery hidden in the course 🕵️", path: "puzzle" },
      { label: "Saving my squad in a race against time ⏱️", path: "action" },
      { label: "Unlocking gear and new powers 🗡️", path: "gamer" },
    ],
  },
  {
    question: "Pick your vibe:",
    options: [
      { label: "Spy mission 🧩", path: "puzzle" },
      { label: "Disaster rescue ⚡", path: "action" },
      { label: "Fantasy RPG adventure 🐉", path: "gamer" },
    ],
  },
];
